import { useAuthStore } from '../stores/auth-store.ts';

const API_BASE = '/api/v1';

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function isApiForbidden(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.code === 'FORBIDDEN');
}

let refreshPromise: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  const { refreshToken, setTokens, clearAuth } = useAuthStore.getState();
  if (!refreshToken) {
    clearAuth();
    throw new ApiError(401, 'No refresh token');
  }

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      clearAuth();
      window.location.href = '/login';
      throw new ApiError(401, 'Refresh failed');
    }

    const data = await res.json();
    setTokens(data.accessToken, data.refreshToken);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    clearAuth();
    window.location.href = '/login';
    throw new ApiError(401, 'Refresh failed');
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { accessToken } = useAuthStore.getState();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Auto-refresh on 401
  if (res.status === 401 && accessToken) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    await refreshPromise;

    // Retry with new token
    const newToken = useAuthStore.getState().accessToken;
    headers['Authorization'] = `Bearer ${newToken}`;
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    let errorMessage: string;
    let errorCode: string | undefined =
      typeof errorBody.code === 'string' ? errorBody.code : undefined;
    if (typeof errorBody.error === 'string') {
      errorMessage = errorBody.error;
    } else if (typeof errorBody.error === 'object' && errorBody.error !== null) {
      if (typeof errorBody.error.code === 'string') {
        errorCode = errorBody.error.code;
      }
      if (errorBody.error.message) {
        errorMessage = errorBody.error.message;
      } else if (errorBody.error.issues) {
        errorMessage = errorBody.error.issues.map((i: { path?: string[]; message?: string }) =>
          `${i.path?.join('.') || ''}: ${i.message || 'invalid'}`
        ).join('; ');
      } else {
        errorMessage = errorBody.message || res.statusText;
      }
    } else {
      errorMessage = errorBody.message || res.statusText;
    }
    throw new ApiError(res.status, errorMessage, errorCode);
  }

  // Handle 204 No Content
  if (res.status === 204) return undefined as T;

  const json = await res.json();
  // Unwrap { success, data } envelope if present
  if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
    // Preserve pagination meta when present (e.g. { success, data, meta: { page, total, ... } })
    if ('meta' in json && json.meta && typeof json.meta === 'object') {
      return { data: json.data, ...json.meta } as T;
    }
    // Some list endpoints (e.g. /servers/groups) put totalCount next to data without meta.
    if ('totalCount' in json && json.totalCount != null) {
      return { data: json.data, totalCount: json.totalCount } as T;
    }
    return json.data as T;
  }
  return json as T;
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
